import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { palette, cssVar } from '@/components/charts/chartTheme';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { ProductForecastMetric, ProductForecastResponse } from '@/api/bi';

interface ForecastChartProps {
  data: ProductForecastResponse | undefined;
  height?: number;
  /** Controlled metric; when omitted the chart renders its own toggle. */
  metric?: ProductForecastMetric;
  onMetricChange?: (metric: ProductForecastMetric) => void;
}

interface MergedPoint {
  t: string;
  actual?: number;
  yhat?: number;
  band_lo?: number;
  band_span?: number;
  forecast?: boolean;
}

const METRICS: { value: ProductForecastMetric; label: string }[] = [
  { value: 'units', label: 'Units' },
  { value: 'gmv', label: 'GMV' },
  { value: 'orders', label: 'Orders' },
];

function historyValue(
  h: ProductForecastResponse['history'][number],
  metric: ProductForecastMetric,
): number {
  if (metric === 'gmv') return h.gmv ?? 0;
  if (metric === 'orders') return h.orders ?? 0;
  return h.units;
}

function forecastValues(
  f: ProductForecastResponse['forecast'][number],
  metric: ProductForecastMetric,
): { yhat: number; lo: number; hi: number } {
  if (metric === 'gmv') return { yhat: f.gmv_yhat ?? 0, lo: f.gmv_lo ?? 0, hi: f.gmv_hi ?? 0 };
  if (metric === 'orders') return { yhat: f.orders_yhat ?? 0, lo: f.orders_lo ?? 0, hi: f.orders_hi ?? 0 };
  return { yhat: f.yhat, lo: f.lo, hi: f.hi };
}

function metricLabel(metric: ProductForecastMetric): string {
  if (metric === 'gmv') return 'GMV (actual)';
  if (metric === 'orders') return 'Orders (actual)';
  return 'Units (actual)';
}

function formatMetricValue(metric: ProductForecastMetric, v: number): string {
  if (metric === 'gmv') return fmt.npr(v);
  return fmt.number(v);
}

/**
 * Demand forecast chart — actual daily history (solid) continued by the
 * projected estimate (dashed) with a shaded 80% confidence band.
 */
export function ForecastChart({
  data,
  height = 300,
  metric: controlledMetric,
  onMetricChange,
}: ForecastChartProps) {
  const [internalMetric, setInternalMetric] = useState<ProductForecastMetric>('units');
  const metric = controlledMetric ?? internalMetric;
  const setMetric = onMetricChange ?? setInternalMetric;

  const merged = useMemo<MergedPoint[]>(() => {
    if (!data) return [];
    const out: MergedPoint[] = data.history.map((h) => ({
      t: h.t,
      actual: historyValue(h, metric),
    }));
    const lastActual = out.at(-1);
    if (lastActual?.actual != null) lastActual.yhat = lastActual.actual;
    for (const f of data.forecast) {
      const fc = forecastValues(f, metric);
      out.push({
        t: f.t,
        yhat: fc.yhat,
        band_lo: fc.lo,
        band_span: Math.max(0, fc.hi - fc.lo),
        forecast: true,
      });
    }
    return out;
  }, [data, metric]);

  const firstForecast = data?.forecast[0]?.t;

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-surface p-0.5 text-[11px]">
          {METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMetric(m.value)}
              aria-pressed={metric === m.value}
              className={cn(
                'press inline-flex h-6 items-center rounded-sm px-2 font-medium transition-colors focus-ring',
                metric === m.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <ComposedChart data={merged} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke={palette.grid()} vertical={false} />
            <XAxis
              dataKey="t"
              tickFormatter={(s: string) => fmt.date(s, 'd MMM')}
              stroke={palette.axis()}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              minTickGap={24}
            />
            <YAxis
              stroke={palette.axis()}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              width={48}
              tickFormatter={(v: number) => (metric === 'gmv' ? fmt.compact(v) : fmt.compact(v))}
            />
            <Tooltip
              cursor={{ stroke: palette.border(), strokeWidth: 1 }}
              content={(props) => (
                <ChartTooltip
                  {...props}
                  formatValue={(v) => formatMetricValue(metric, Number(v))}
                />
              )}
            />
            <Area
              dataKey="band_lo"
              stackId="band"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
              activeDot={false}
            />
            <Area
              dataKey="band_span"
              stackId="band"
              stroke="none"
              fill={cssVar('info', 0.16)}
              isAnimationActive={false}
              activeDot={false}
              name="80% band"
            />
            {firstForecast ? (
              <ReferenceLine x={firstForecast} stroke={palette.axis()} strokeDasharray="3 3" />
            ) : null}
            <Line
              dataKey="actual"
              name={metricLabel(metric)}
              type="monotone"
              stroke={palette.primary()}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              dataKey="yhat"
              name="Forecast"
              type="monotone"
              stroke={cssVar('info')}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
